import { useState, useCallback } from 'react';
import type { Message, Conversation, Source } from '../types/agent';
import * as agentApi from '../services/agentApi';

interface UseAgentReturn {
  // State
  messages: Message[];
  conversations: Conversation[];
  currentConversation: Conversation | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  sendMessage: (message: string) => Promise<void>;
  loadConversations: () => Promise<void>;
  loadConversation: (conversationId: string) => Promise<void>;
  deleteConversation: (conversationId: string) => Promise<void>;
  startNewConversation: () => void;
  clearError: () => void;
}

export function useAgent(userId: string, bankId?: string): UseAgentReturn {
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Send a message to the agent
   */
  const sendMessage = useCallback(async (messageText: string) => {
    if (!messageText.trim()) return;

    setIsLoading(true);
    setError(null);

    // Add user message immediately to UI
    const userMessage: Message = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: messageText,
      createdAt: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);

    try {
      const response = await agentApi.sendMessage({
        conversationId: currentConversation?.id,
        message: messageText,
        userId,
        context: { bankId }
      });

      // Update conversation ID if it's a new conversation
      if (!currentConversation || currentConversation.id !== response.conversationId) {
        setCurrentConversation({
          id: response.conversationId,
          title: messageText.substring(0, 50) + '...',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      }

      // Add assistant response
      const assistantMessage: Message = {
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: response.response,
        metadata: response.sources ? { sources: response.sources } : undefined,
        createdAt: new Date().toISOString()
      };

      setMessages(prev => {
        // Replace temp user message with actual one, then add assistant response
        const withoutTemp = prev.filter(m => m.id !== userMessage.id);
        return [...withoutTemp, userMessage, assistantMessage];
      });

    } catch (err: any) {
      setError(err.message || 'Errore nell\'invio del messaggio');
      // Remove the temporary user message on error
      setMessages(prev => prev.filter(m => m.id !== userMessage.id));
    } finally {
      setIsLoading(false);
    }
  }, [userId, bankId, currentConversation]);

  /**
   * Load user's conversation history
   */
  const loadConversations = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await agentApi.getConversations(userId);
      setConversations(response.conversations);
    } catch (err: any) {
      setError(err.message || 'Errore nel caricamento delle conversazioni');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  /**
   * Load a specific conversation
   */
  const loadConversation = useCallback(async (conversationId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await agentApi.getConversation(conversationId, userId);
      setCurrentConversation(response.conversation);
      setMessages(response.conversation.messages || []);
    } catch (err: any) {
      setError(err.message || 'Errore nel caricamento della conversazione');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  /**
   * Delete a conversation
   */
  const deleteConversation = useCallback(async (conversationId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      await agentApi.deleteConversation(conversationId, userId);

      // Remove from list
      setConversations(prev => prev.filter(c => c.id !== conversationId));

      // Clear current conversation if it's the deleted one
      if (currentConversation?.id === conversationId) {
        setCurrentConversation(null);
        setMessages([]);
      }
    } catch (err: any) {
      setError(err.message || 'Errore nell\'eliminazione della conversazione');
    } finally {
      setIsLoading(false);
    }
  }, [userId, currentConversation]);

  /**
   * Start a new conversation
   */
  const startNewConversation = useCallback(() => {
    setCurrentConversation(null);
    setMessages([]);
    setError(null);
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    messages,
    conversations,
    currentConversation,
    isLoading,
    error,
    sendMessage,
    loadConversations,
    loadConversation,
    deleteConversation,
    startNewConversation,
    clearError
  };
}
